import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Star, Loader2, MessageSquareReply, ExternalLink, RefreshCw, Info, CheckCircle2 } from 'lucide-react';

const Stars = ({ n = 0 }) => (
  <span className="inline-flex items-center gap-0.5" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? 'fill-emerald-500 text-emerald-500' : 'text-slate-300'}`} />
    ))}
  </span>
);

export default function TrustpilotReviews() {
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState({});
  const [source, setSource] = useState('');
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r, s] = await Promise.all([
        api.get('/trustpilot/profile'),
        api.get('/trustpilot/reviews?limit=30'),
        api.get('/trustpilot/status'),
      ]);
      setProfile(p.data);
      setReviews(r.data.reviews || []);
      setSource(r.data.source);
      setStatus(s.data);
    } catch (e) {
      toast.error('Could not load Trustpilot reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submitReply = async (reviewId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const res = await api.post(`/trustpilot/reviews/${reviewId}/reply`, { reply_text: replyText.trim() });
      if (res.data.live) {
        toast.success('Reply posted to Trustpilot');
      } else {
        toast.info('Reply saved locally', { description: res.data.message });
      }
      setReviews((prev) => prev.map((rv) => rv.review_id === reviewId
        ? { ...rv, company_reply: { text: replyText.trim() } } : rv));
      setReplyOpen(null);
      setReplyText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally {
      setSending(false);
    }
  };

  const isMock = source === 'mock';

  return (
    <Layout>
    <div className="space-y-6 max-w-5xl" data-testid="trustpilot-reviews-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Star className="w-6 h-6 text-emerald-500 fill-emerald-500" /> Trustpilot Reviews
          </h1>
          <p className="text-sm text-slate-500">View and reply to your Trustpilot reviews without leaving the CRM.</p>
        </div>
        <div className="flex items-center gap-2">
          {profile?.profile_url && (
            <Button variant="outline" size="sm" asChild data-testid="open-trustpilot-btn">
              <a href={profile.profile_url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4 mr-1.5" /> Open on Trustpilot
              </a>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-reviews-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {isMock && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 px-4 py-3 text-sm text-amber-800 dark:text-amber-300" data-testid="trustpilot-demo-banner">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span><strong>Demo data.</strong> Trustpilot API credentials aren't configured yet, so these are sample reviews.
          Add the Trustpilot Business API keys to your backend environment to show live reviews and post replies.</span>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <>
          {profile && (
            <Card data-testid="trustpilot-profile-card">
              <CardContent className="py-5 flex flex-wrap items-center gap-x-10 gap-y-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">TrustScore</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{profile.trust_score ?? '—'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Rating</p>
                  <Stars n={profile.stars || 0} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Total reviews</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white">{profile.number_of_reviews ?? '—'}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="font-semibold text-slate-700 dark:text-slate-200">{profile.display_name}</p>
                  <Badge variant={status.reply_live ? 'default' : 'secondary'} className="mt-1">
                    {status.reply_live ? 'Replies: live' : 'Replies: demo mode'}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="space-y-3" data-testid="trustpilot-reviews-list">
            {reviews.length === 0 && (
              <p className="text-sm text-slate-400 italic py-6">No reviews yet.</p>
            )}
            {reviews.map((rv) => (
              <Card key={rv.review_id} data-testid={`review-card-${rv.review_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">{rv.title || 'Review'}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Stars n={rv.stars || 0} />
                        <span className="text-xs text-slate-400">
                          {rv.consumer_name || 'Anonymous'} · {rv.created_at ? new Date(rv.created_at).toLocaleDateString() : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-300">{rv.text}</p>

                  {rv.company_reply ? (
                    <div className="rounded-md border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm" data-testid={`review-reply-${rv.review_id}`}>
                      <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mb-1">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Your reply
                      </p>
                      <p className="text-slate-600 dark:text-slate-300">{rv.company_reply.text}</p>
                    </div>
                  ) : replyOpen === rv.review_id ? (
                    <div className="space-y-2" data-testid={`reply-composer-${rv.review_id}`}>
                      <Textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write a public reply…"
                        rows={3}
                        data-testid={`reply-input-${rv.review_id}`}
                      />
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => submitReply(rv.review_id)} disabled={sending} data-testid={`reply-submit-${rv.review_id}`}>
                          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MessageSquareReply className="w-4 h-4 mr-1.5" />}
                          Post reply
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setReplyOpen(null); setReplyText(''); }}>Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => { setReplyOpen(rv.review_id); setReplyText(''); }} data-testid={`reply-open-${rv.review_id}`}>
                      <MessageSquareReply className="w-4 h-4 mr-1.5" /> Reply
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
    </Layout>
  );
}
